import React from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '../../utils/lazyWithRetry';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export default class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    try {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    } catch {
      // Sentry not initialized or unavailable — silently ignore
    }
    // Note: auto-reload for chunk errors is handled by lazyWithRetry's
    // importWithRetry — the boundary only shows fallback UI.
  }

  handleRetry = () => {
    this.setState({ hasError: false, isChunkError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
          <div className="text-center space-y-4">
            <p className="text-lg text-gray-300">
              {this.state.isChunkError
                ? 'Приложение обновлено / App has been updated'
                : 'Failed to load page / Не удалось загрузить страницу'}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                {this.state.isChunkError ? 'Reload / Обновить' : 'Reload page'}
              </button>
              {!this.state.isChunkError && (
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
